import { Card, CardContent } from '@/components/ui/card';
import { formatearCompacto } from './GraficoBarrasIngresoGasto';

export interface MesComparativo {
  anio: number;
  mes: number;
  produccion: number;
  costo: number;
}

const NOMBRES_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

export function fmt(v: number): string {
  if (!v) return '—';
  const s = Math.round(Math.abs(v))
    .toLocaleString('es-CR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ');
  return `${v < 0 ? '-' : ''}₡${s}`;
}

const etiqueta = (m: MesComparativo) => `${NOMBRES_MES[m.mes - 1]} ${String(m.anio).slice(2)}`;

/** Barras agrupadas Producción (verde) vs Costo real (rojo) por mes, SVG propio. */
function GraficoProduccionCosto({ meses }: { meses: MesComparativo[] }) {
  const ancho = Math.max(640, meses.length * 56 + 70);
  const alto = 260;
  const m = { t: 12, r: 8, b: 30, l: 56 };
  const w = ancho - m.l - m.r;
  const h = alto - m.t - m.b;
  const max = Math.max(1, ...meses.flatMap((x) => [x.produccion, x.costo]));
  const paso = w / Math.max(1, meses.length);
  const bw = Math.min(22, paso / 2.6);
  const y = (v: number) => m.t + h - (Math.max(0, v) / max) * h;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${ancho} ${alto}`}
        width={ancho}
        height={alto}
        role="img"
        aria-label="Producción vs costo real por mes"
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={m.l} x2={ancho - m.r} y1={y(t)} y2={y(t)} stroke="#e4e4e7" />
            <text x={m.l - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill="#71717a">
              {formatearCompacto(t)}
            </text>
          </g>
        ))}
        {meses.map((x, i) => {
          const cx = m.l + paso * i + paso / 2;
          return (
            <g key={`${x.anio}-${x.mes}`}>
              <title>{`${etiqueta(x)} · Producción ${fmt(x.produccion)} · Costo ${fmt(x.costo)}`}</title>
              <rect
                x={cx - bw - 1}
                y={y(x.produccion)}
                width={bw}
                height={m.t + h - y(x.produccion)}
                fill="#22c55e"
                rx="2"
              />
              <rect
                x={cx + 1}
                y={y(x.costo)}
                width={bw}
                height={m.t + h - y(x.costo)}
                fill="#f87171"
                rx="2"
              />
              <text x={cx} y={alto - 12} textAnchor="middle" fontSize="10" fill="#52525b">
                {etiqueta(x)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="flex items-center gap-4 pt-1 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-green-500" /> Producción
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-red-400" /> Costo real
        </span>
      </div>
    </div>
  );
}

/** Tarjeta con el gráfico Producción vs Costo real por mes. */
export function ComparativoProduccionCosto({ meses }: { meses: MesComparativo[] }) {
  if (meses.length === 0) return null;
  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Producción vs Costo real</h2>
          <p className="text-xs text-muted-foreground">
            Costo real directo ejecutado en el mes (sin Servicios Centrales ni partidas indirectas),
            contra la producción del mes. Respeta los filtros globales, incluido el estado de venta.
          </p>
        </div>
        <GraficoProduccionCosto meses={meses} />
      </CardContent>
    </Card>
  );
}
